import { AppTopbar } from './app.topbar';
import { LayoutService } from '../service/layout.service';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { ModuleMenuComponent } from '../../modules/shared/module-menu/module-menu.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

const BREAKPOINT = 992;

@Component({
  selector: '[app-sidebar]',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ToggleSwitchModule,
    TooltipModule,
    AppTopbar,
    ModuleMenuComponent
  ],
  template: `<div
    class="layout-sidebar"
    (mouseenter)="onMouseEnter()"
    (mouseleave)="onMouseLeave()"
  >
    <div class="sidebar-header">
      <a class="logo" [routerLink]="['/']">
        <img
          class="logo-image"
          src="/assets/images/Aline_logo-12.png"
          alt="Aline"
        />
      </a>
      <p-toggleswitch
        class="layout-sidebar-anchor"
        [ngModel]="isAnchored()"
        (ngModelChange)="onAnchorToggle()"
        [pTooltip]="isAnchored() ? 'Unpin menu' : 'Pin menu open'"
        tooltipPosition="bottom"
      />
    </div>

    <div
      #menuContainer
      class="layout-menu-container"
      (scroll)="onMenuScroll()"
    >
      <app-module-menu [module]="module"></app-module-menu>
    </div>
    <div app-topbar *ngIf="isHorizontal()"></div>
  </div>`
})
export class AppSidebar implements OnInit, OnDestroy {
  @Input() module: string | null = null;

  layoutService = inject(LayoutService);

  router = inject(Router);

  el = inject(ElementRef);

  @ViewChild('menuContainer') menuContainer!: ElementRef;

  private timeout: any = null;

  private observer: IntersectionObserver | null = null;

  private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  private mediaQuery = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);

  private destroy$ = new Subject<void>();

  isHorizontal = computed(() => this.layoutService.isHorizontal());

  isAnchored = computed(() => this.layoutService.layoutState().anchored);

  menuTheme = computed(() => this.layoutService.layoutConfig().menuTheme);

  constructor() {
    effect(() => {
      const hasOpenOverlay = this.layoutService.hasOpenOverlay();
      const mobileMenuActive = this.layoutService.layoutState().mobileMenuActive;

      if (this.layoutService.isDesktop()) {
        if (hasOpenOverlay) {
          this.bindOutsideClickListener();
        } else {
          this.unbindOutsideClickListener();
        }
      } else {
        if (mobileMenuActive) {
          this.bindOutsideClickListener();
        } else {
          this.unbindOutsideClickListener();
        }
      }
    });

    effect(() => {
      const hasOpenOverlaySubmenu = this.layoutService.hasOpenOverlaySubmenu();
      if (this.layoutService.isDesktop()) {
        if (hasOpenOverlaySubmenu) {
          setTimeout(() => this.setupIntersectionObserver());
        } else {
          this.unbindObserver();
        }
      }
    });
  }

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;
        this.onRouteChange(navEvent.urlAfterRedirects);
      });

    this.onRouteChange(this.router.url);

    this.mediaQuery.addEventListener('change', this.screenChangeListener);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.unbindOutsideClickListener();
    this.unbindObserver();
    this.mediaQuery.removeEventListener('change', this.screenChangeListener);
  }

  // Menu items key active state off a stable item id (see AppMenuitem.fullPath),
  // not the route path, since Aline's menu tree isn't 1:1 with URL segments.
  // So unlike upstream Diamond, this only clears activePath when leaving an
  // overlay-submenu mode (slim/compact/horizontal) - AppMenuitem's own
  // routerLink-match effect is what sets activePath for static/overlay modes.
  private onRouteChange(path: string) {
    const clearActivePath =
      this.layoutService.hasOverlaySubmenu() && this.layoutService.isDesktop();

    this.layoutService.layoutState.update((val) => ({
      ...val,
      activePath: clearActivePath ? null : val.activePath,
      overlayMenuActive: false,
      mobileMenuActive: false,
      menuHoverActive: false
    }));
  }

  private screenChangeListener = () => {
    if (this.layoutService.hasOverlaySubmenu()) {
      this.layoutService.layoutState.update((val) => ({
        ...val,
        menuHoverActive: false
      }));
      this.unbindOutsideClickListener();
      this.unbindObserver();
    }
  };

  private bindOutsideClickListener() {
    if (!this.outsideClickListener) {
      this.outsideClickListener = (event: MouseEvent) => {
        if (this.isOutsideClicked(event)) {
          if (this.layoutService.isDesktop()) {
            this.layoutService.layoutState.update((val) => ({
              ...val,
              overlayMenuActive: false
            }));

            if (this.layoutService.hasOverlaySubmenu()) {
              this.layoutService.layoutState.update((val) => ({
                ...val,
                menuHoverActive: false
              }));
            }
          } else {
            this.layoutService.layoutState.update((val) => ({
              ...val,
              mobileMenuActive: false
            }));
          }
        }
      };

      document.addEventListener('click', this.outsideClickListener);
    }
  }

  private unbindOutsideClickListener() {
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
      this.outsideClickListener = null;
    }
  }

  private isOutsideClicked(event: MouseEvent): boolean {
    const topbarButtonEl = document.querySelector('.topbar-left > a');
    const sidebarEl = this.el.nativeElement;

    return !(
      sidebarEl?.isSameNode(event.target as Node) ||
      sidebarEl?.contains(event.target as Node) ||
      topbarButtonEl?.isSameNode(event.target as Node) ||
      topbarButtonEl?.contains(event.target as Node)
    );
  }

  onMouseEnter() {
    if (!this.layoutService.layoutState().anchored) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.layoutService.layoutState.update((state) => ({
        ...state,
        sidebarExpanded: true
      }));
    }
  }

  onMouseLeave() {
    if (!this.layoutService.layoutState().anchored && !this.timeout) {
      this.timeout = setTimeout(() => {
        this.layoutService.layoutState.update((state) => ({
          ...state,
          sidebarExpanded: false
        }));
      }, 300);
    }
  }

  onAnchorToggle() {
    this.layoutService.layoutState.update((state) => ({
      ...state,
      anchored: !state.anchored
    }));
  }

  onMenuScroll() {
    if (this.menuContainer?.nativeElement) {
      if (this.layoutService.isHorizontal()) {
        const scrollLeft = this.menuContainer.nativeElement.scrollLeft;
        this.menuContainer.nativeElement.style.setProperty(
          '--menu-scroll-x',
          `-${scrollLeft}px`
        );
      } else {
        const scrollTop = this.menuContainer.nativeElement.scrollTop;
        this.menuContainer.nativeElement.style.setProperty(
          '--menu-scroll-y',
          `-${scrollTop}px`
        );
      }
    }
  }

  private setupIntersectionObserver() {
    if (!this.menuContainer?.nativeElement) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    const activeMenuItem = this.menuContainer.nativeElement.querySelector(
      '.layout-root-menuitem.active-menuitem'
    );
    if (!activeMenuItem) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            this.layoutService.isDesktop() &&
            !entry.isIntersecting &&
            this.layoutService.hasOverlaySubmenu() &&
            this.layoutService.layoutState().activePath
          ) {
            this.layoutService.layoutState.update((val) => ({
              ...val,
              activePath: null
            }));
          }
        });
      },
      {
        root: this.menuContainer.nativeElement,
        threshold: 0
      }
    );

    this.observer.observe(activeMenuItem);
  }

  private unbindObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
